import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Swords, Trash2, ChevronRight } from "lucide-react";

const STATUS = { draft: ["Bozza", "bg-zinc-100 text-zinc-600"], pools: ["Gironi", "bg-amber-100 text-amber-800"], direct_elimination: ["Eliminazione diretta", "bg-blue-100 text-blue-800"], completed: ["Conclusa", "bg-emerald-100 text-emerald-800"] };
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function CompetitiveSessionsList() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: new Date().toISOString().slice(0, 10), weapon: "", pool_hits: 5, de_hits: 15 });

  const load = () => api.get("/competitive-sessions").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Inserisci un nome"); return; }
    try {
      const { data } = await api.post("/competitive-sessions", { ...form, weapon: form.weapon || null, participants: [] });
      toast.success("Simulazione creata");
      setOpen(false);
      nav(`/scherma/simulazione/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare la simulazione e tutti i suoi assalti?")) return;
    try { await api.delete(`/competitive-sessions/${id}`); toast.success("Simulazione eliminata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="competitive-sessions">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2"><Swords className="w-5 h-5" />Simulazioni gara</h2>
          <p className="text-sm text-zinc-500">Gironi all'italiana + eliminazione diretta. Ogni incontro è un assalto canonico nello storico degli atleti.</p>
        </div>
        <Button data-testid="csess-new" onClick={() => setOpen(true)} className="bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-1" />Nuova simulazione</Button>
      </div>

      {items === null ? <div className="text-sm text-zinc-500">Caricamento…</div> : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-8 text-center text-sm text-zinc-500" data-testid="csess-empty">Nessuna simulazione. Creane una per iniziare.</div>
      ) : (
        <div className="space-y-2">
          {items.map((s) => {
            const [label, cls] = STATUS[s.status] || STATUS.draft;
            return (
              <div key={s.id} data-testid={`csess-item-${s.id}`} className="flex items-center gap-3 bg-white border border-zinc-200 rounded-md px-4 py-3 hover:border-zinc-400 transition">
                <button className="flex-1 min-w-0 text-left" data-testid={`csess-open-${s.id}`} onClick={() => nav(`/scherma/simulazione/${s.id}`)}>
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{fmtDate(s.date)}{s.weapon ? ` · ${s.weapon}` : ""} · {(s.participants || []).length} partecipanti · girone a {s.pool_hits} · ED a {s.de_hits}{s.training_id ? " · collegata a seduta" : ""}</div>
                </button>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                <button data-testid={`csess-delete-${s.id}`} onClick={() => remove(s.id)} className="p-1.5 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                <ChevronRight className="w-4 h-4 text-zinc-300" />
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="csess-create-dialog">
          <DialogHeader><DialogTitle>Nuova simulazione gara</DialogTitle><DialogDescription>Imposta nome, data e formato. I partecipanti si aggiungono dopo.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="csess-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Simulazione Spada U14" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" data-testid="csess-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Arma</Label>
                <Select value={form.weapon || "none"} onValueChange={(v) => setForm({ ...form, weapon: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="csess-weapon"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="fioretto">Fioretto</SelectItem><SelectItem value="spada">Spada</SelectItem><SelectItem value="sciabola">Sciabola</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Stoccate girone</Label><Input type="number" min={1} data-testid="csess-pool-hits" value={form.pool_hits} onChange={(e) => setForm({ ...form, pool_hits: parseInt(e.target.value || 5) })} /></div>
              <div><Label>Stoccate ED</Label>
                <Select value={String(form.de_hits)} onValueChange={(v) => setForm({ ...form, de_hits: parseInt(v) })}>
                  <SelectTrigger data-testid="csess-de-hits"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="10">10</SelectItem><SelectItem value="15">15</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="csess-create" onClick={create} className="bg-black hover:bg-zinc-800">Crea</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
